import { v4 as uuidv4 } from "uuid";
import { formatCurrency } from "../../../utils/formatCurrency";
import { RecordingStatus } from "../terminal-payment/terminalPaymentApi";
import { OrderPayment } from "../ordersGridTypes.types";
import { validatePayments } from "../utils";

export type SplitPayment = {
    payment_method: string;
    amount: number;
};

export type SplitPaymentRow = SplitPayment & {
    id: string;
};

export type ChargeProgress = "open" | "paid" | "closed";

type SplitPaymentsValidation = {
    isValid: boolean;
    error?: string;
};

export const SPLIT_PAYMENT_METHODS: Array<{ value: string; label: string }> = [
    { value: "cash", label: "Dinheiro" },
    { value: "transferencia_bancaria", label: "Transferência Bancária" },
];

export const SPLIT_PAYMENTS_MAX_ROWS = 5;

const CENTS_TOLERANCE = 1e-6;
const INVALID_AMOUNT_MESSAGE = "Cada pagamento tem de ter um valor acima de 0 €, com no máximo duas casas decimais.";
const SPLIT_NOT_BELOW_TOTAL_MESSAGE = "Os outros pagamentos têm de ser inferiores ao total da compra.";

const toCents = (amount: number) => {
    const cents = Math.round(amount * 100);
    return cents;
};

const isValidSplitAmount = (amount: number) => {
    const isWholeCents = Math.abs(amount * 100 - toCents(amount)) < CENTS_TOLERANCE;
    const isValid = Number.isFinite(amount) && amount > 0 && isWholeCents;
    return isValid;
};

export const calculateRemainingAmount = (orderTotal: number, splitPaymentRows: Array<SplitPayment>) => {
    const splitTotalInCents = splitPaymentRows.reduce((sumInCents, row) => sumInCents + toCents(Number(row.amount) || 0), 0);
    const remainingAmount = (toCents(orderTotal) - splitTotalInCents) / 100;
    return remainingAmount;
};

export const validateSplitPayments = (orderTotal: number, splitPaymentRows: Array<SplitPaymentRow>, electronicPaymentMethod: string) => {
    const remainingAmount = calculateRemainingAmount(orderTotal, splitPaymentRows);
    const allPayments: Array<OrderPayment> = [
        ...splitPaymentRows,
        { id: "electronic-payment", payment_method: electronicPaymentMethod, amount: remainingAmount },
    ];
    const paymentsValidation = validatePayments(allPayments, orderTotal);
    if (!paymentsValidation.isValid) return { isValid: false, error: paymentsValidation.error } as SplitPaymentsValidation;
    if (!splitPaymentRows.every((row) => isValidSplitAmount(Number(row.amount)))) {
        return { isValid: false, error: INVALID_AMOUNT_MESSAGE } as SplitPaymentsValidation;
    }
    const validation: SplitPaymentsValidation = remainingAmount > 0 ? { isValid: true } : { isValid: false, error: SPLIT_NOT_BELOW_TOTAL_MESSAGE };
    return validation;
};

export const toSplitPayments = (splitPaymentRows: Array<SplitPaymentRow>) => {
    const splitPayments: Array<SplitPayment> = splitPaymentRows.map((row) => ({ payment_method: row.payment_method, amount: Number(row.amount) }));
    return splitPayments;
};

export const describeSplitPayments = (splitPayments: Array<SplitPayment>) => {
    const description = splitPayments
        .map((splitPayment) => {
            const methodLabel = SPLIT_PAYMENT_METHODS.find((method) => method.value === splitPayment.payment_method)?.label ?? splitPayment.payment_method;
            const paymentDescription = `${methodLabel} ${formatCurrency(splitPayment.amount)}`;
            return paymentDescription;
        })
        .join("; ");
    return description;
};

export const toSplitPaymentRows = (splitPayments: Array<SplitPayment>) => {
    const rows: Array<SplitPaymentRow> = splitPayments.map((splitPayment) => ({ id: uuidv4(), ...splitPayment }));
    return rows;
};

const resolveSplitPaymentsNoteLabel = (chargeProgress: ChargeProgress, recordingStatus: RecordingStatus | null) => {
    if (chargeProgress === "open") return "Outros pagamentos (ficam registados quando o pagamento for aceite)";
    if (chargeProgress === "closed") return "Outros pagamentos não registados";
    const paidLabel = recordingStatus === "recorded" ? "Outros pagamentos registados" : "Outros pagamentos ainda não registados";
    return paidLabel;
};

export const buildSplitPaymentsNote = (splitPayments: Array<SplitPayment>, chargeProgress: ChargeProgress, recordingStatus: RecordingStatus | null) => {
    const note = `${resolveSplitPaymentsNoteLabel(chargeProgress, recordingStatus)}: ${describeSplitPayments(splitPayments)}`;
    return note;
};
